import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  CheckOutlined,
  ChevronLeft,
  CreditCard,
  History,
  Warning,
  Star,
  RocketLaunch,
  AutoAwesome,
  Verified
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
  IconButton,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  Stack,
  Slide,
  Modal
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCurrentUser,
  fetchUserBillingDetails
} from "../../features/auth/authSlice";
import {
  selectCurrentUser,
  selectCurrentUserBillingDetails
} from "../../features/auth/authSelectors";
import dayjs from "dayjs";
import CardLists from "./CardLists";
import useThemeColor from "../../utils/useTheme";
import {
  addSubscription,
  cancelSubscription,
  reactivateSubscription,
  createUpgradePurchase
} from "../../api/userApi";
import {
  allowedCountries,
  countryRates
} from "../../utils/countryRatesMapping";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ConfirmModal = ({ open, handleClose, handleConfirm, styles }) => {
  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: styles.lightGreyColor,
          boxShadow: 24,
          p: 2.2,
          borderRadius: 2,
          minWidth: 240,
          textAlign: "center"
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, color: styles.color }}>
          Are you sure?
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            onClick={handleConfirm}
            sx={{
              p: "8px 14px",
              background: "#368AD0",
              color: "#fcfcfc",
              textTransform: "none",
              fontWeight: "bold",
              "&:hover": {
                background: "#2D74B2"
              }
            }}
          >
            Yes
          </Button>
          <Button
            onClick={handleClose}
            sx={{
              p: "8px 14px",
              color: "#368AD0",
              textTransform: "none",
              fontWeight: "bold",
              backgroundColor: "#ffffff",
              "&:hover": {
                backgroundColor: "#f0f0f0"
              }
            }}
          >
            No
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const UpgradePlanModal = ({ open, handleClose, handleUpgrade, styles, loading }) => {
  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: styles.background,
          boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px ${styles.primaryColor}20`,
          p: 0,
          borderRadius: 4,
          minWidth: 380,
          maxWidth: 420,
          overflow: "hidden",
          border: `1px solid ${styles.lightGreyColor}`
        }}
      >
        {/* Premium Header with Theme Colors */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${styles.primaryColor} 0%, ${styles.primaryColor}CC 50%, ${styles.primaryColor}99 100%)`,
            p: 3,
            textAlign: "center",
            position: "relative",
            overflow: "hidden"
          }}
        >
          {/* Decorative Floating Elements */}
          <Box
            sx={{
              position: "absolute",
              top: -20,
              right: -20,
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
              filter: "blur(20px)"
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -30,
              left: -30,
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.1)",
              filter: "blur(25px)"
            }}
          />
          
          {/* Sparkle Effects */}
          <AutoAwesome
            sx={{
              position: "absolute",
              top: 20,
              left: 30,
              fontSize: 20,
              color: "rgba(255,255,255,0.6)",
              animation: "sparkle 2s ease-in-out infinite"
            }}
          />
          <AutoAwesome
            sx={{
              position: "absolute",
              top: 50,
              right: 40,
              fontSize: 16,
              color: "rgba(255,255,255,0.5)",
              animation: "sparkle 2.5s ease-in-out infinite"
            }}
          />
          
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.25)",
                backdropFilter: "blur(10px)",
                mb: 2,
                boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
              }}
            >
              <RocketLaunch sx={{ fontSize: 36, color: "#fff" }} />
            </Box>
            <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 0.5, textShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>
              Upgrade to Premium
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.95)", fontWeight: 500 }}>
              Unlock your full potential today
            </Typography>
          </Box>
        </Box>
        
        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* Premium Badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 2,
              py: 0.75,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${styles.primaryColor}15, ${styles.primaryColor}08)`,
              border: `1px solid ${styles.primaryColor}30`,
              mb: 2
            }}
          >
            <Verified sx={{ fontSize: 18, color: styles.primaryColor }} />
            <Typography variant="caption" sx={{ color: styles.color, fontWeight: 600, fontSize: 12 }}>
              PREMIUM YEARLY PLAN
            </Typography>
          </Box>
          
          {/* Price Display */}
          <Box
            sx={{
              textAlign: "center",
              mb: 3,
              p: 2,
              borderRadius: 3,
              background: styles.lightGreyColor,
              border: `2px solid ${styles.primaryColor}20`
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 0.5 }}>
              <Typography sx={{ color: styles.color, fontSize: 20, fontWeight: 600 }}>$</Typography>
              <Typography sx={{ color: styles.color, fontSize: 56, fontWeight: 800, lineHeight: 1 }}>397</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: styles.lightBlack, fontWeight: 500, mt: 0.5 }}>
              per year · Save over 40%
            </Typography>
          </Box>
          
          {/* Features */}
          <Box sx={{ mb: 3 }}>
            {[
              { text: "Full access to all training levels", icon: CheckCircle },
              { text: "Priority customer support", icon: CheckCircle },
              { text: "Exclusive content & updates", icon: CheckCircle }
            ].map((feature, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1.25,
                  px: 2,
                  borderRadius: 2.5,
                  mb: 1.25,
                  background: styles.modBackgroundBlocks,
                  border: `1px solid ${styles.primaryColor}15`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: `${styles.primaryColor}10`,
                    borderColor: `${styles.primaryColor}30`,
                    transform: "translateX(4px)"
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${styles.primaryColor}, ${styles.primaryColor}DD)`
                  }}
                >
                  <feature.icon sx={{ fontSize: 16, color: "#fff" }} />
                </Box>
                <Typography variant="body2" sx={{ color: styles.color, fontWeight: 500, flex: 1 }}>
                  {feature.text}
                </Typography>
              </Box>
            ))}
          </Box>
          
          
          {/* Buttons */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Button
              onClick={handleUpgrade}
              disabled={loading}
              sx={{
                p: "16px 24px",
                background: `linear-gradient(135deg, ${styles.primaryColor} 0%, ${styles.primaryColor}DD 100%)`,
                color: "#fff",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 16,
                borderRadius: 3,
                boxShadow: `0 8px 24px -4px ${styles.primaryColor}60`,
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  width: "100%",
                  height: "100%",
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                  transition: "left 0.5s ease"
                },
                "&:hover": {
                  background: `linear-gradient(135deg, ${styles.primaryColor}DD 0%, ${styles.primaryColor} 100%)`,
                  transform: "translateY(-2px)",
                  boxShadow: `0 12px 32px -4px ${styles.primaryColor}70`,
                  "&::before": {
                    left: "100%"
                  }
                },
                "&:disabled": {
                  background: styles.disabledColor,
                  color: styles.lightBlack
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Upgrade Now 🚀"}
            </Button>
            <Button
              onClick={handleClose}
              sx={{
                p: "12px 24px",
                color: styles.lightBlack,
                textTransform: "none",
                fontWeight: 500,
                borderRadius: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  color: styles.color,
                  backgroundColor: styles.lightGreyColor
                }
              }}
            >
              Maybe Later
            </Button>
          </Box>
        </Box>
        
        {/* Keyframes for sparkle animation */}
        <style>
          {`
            @keyframes sparkle {
              0%, 100% {
                opacity: 0.4;
                transform: scale(1);
              }
              50% {
                opacity: 1;
                transform: scale(1.2);
              }
            }
          `}
        </style>
      </Box>
    </Modal>
  );
};

const ManageBilling = ({ setOpenSubscriptionModal }) => {
  const navigate = useNavigate();
  const styles = useThemeColor();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openReactivateModal, setOpenReactivateModal] = useState(null);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const currentSubscription = useSelector(selectCurrentUserBillingDetails);
  const currentUser = useSelector(selectCurrentUser);

  useEffect(() => {
    dispatch(fetchUserBillingDetails());
  }, [dispatch]);

  // Check if user is eligible for upgrade
  const isUpgradeEligible = useMemo(() => {
    const productName = currentSubscription?.currentSubscription?.[0]?.productName;
    return (
      currentSubscription?.subscriptionStatus === 'active' &&
      currentSubscription?.upgradeToNewYearlyPlan === true &&
      (productName === "Yearly Plan" || productName === "Monthly Plan")
    );
  }, [currentSubscription]);
  const currentCurrency = useMemo(
    () =>
      countryRates[
        allowedCountries?.includes(currentUser?.country) &&
        !currentUser?.bundleUserAmount
          ? currentUser?.country
          : "US"
      ],
    [currentUser]
  );
  const handleCancelSubscription = async () => {
    setLoading(true);
    await cancelSubscription(currentSubscription?.subscriptionId);
    setTimeout(async () => {
      setOpenModal(false);
      setLoading(false);
      setSnackbarMessage("Subscription cancelled successfully.");
      setOpenSnackbar(true);
      dispatch(fetchUserBillingDetails());
      dispatch(fetchCurrentUser());
    }, 1500);
  };
  const handleReactivateSubscription = async () => {
    setLoading(true);
    await reactivateSubscription(currentSubscription?.subscriptionId);
    setTimeout(async () => {
      setOpenConfirmModal(false);
      setLoading(false);
      setSnackbarMessage("Subscription cancellation revoked successfully.");
      setOpenSnackbar(true);
      dispatch(fetchUserBillingDetails());
      dispatch(fetchCurrentUser());
    }, 1500);
  };

  const handleRenewSubscription = async () => {
    try {
      setLoading(true);
      const { session } = await addSubscription(
        openReactivateModal,
        currentUser?.email,
        false,
        true
      );
      if (session?.url) window.location.href = session?.url;
      else {
        setSnackbarMessage("Subscription Reactivated successfully.");
        setOpenSnackbar(true);
        setTimeout(() => {
          dispatch(fetchUserBillingDetails());
        }, 2000);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setOpenReactivateModal(null);
      setLoading(false);
    }
  };

  const handleUpgradePurchase = async () => {
    try {
      setUpgradeLoading(true);
      const { session } = await createUpgradePurchase();
      if (session?.url) {
        window.location.href = session.url;
      } else {
        setSnackbarMessage("Failed to create upgrade session.");
        setOpenSnackbar(true);
      }
    } catch (err) {
      console.log(err);
      setSnackbarMessage("Upgrade failed. Please try again.");
      setOpenSnackbar(true);
    } finally {
      setUpgradeLoading(false);
    }
  };

  const renderBillingHistoryTable = () => {
    const history = currentSubscription?.billingHistory || [];
    return (
      <Card
        sx={{ boxShadow: "none", mt: 4, mx: 3, background: styles.background }}
        elevation={2}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <History sx={{ mr: 1, color: "#1976d2" }} />
            <Typography variant="h6" fontWeight={500} color={styles.color}>
              Billing History
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: styles.color }}>
                    Amount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: styles.color }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: styles.color }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: styles.color }}>
                    Description
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((record, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ color: styles.color }}>
                      {currentCurrency?.symbol}
                      {record.amount}
                    </TableCell>
                    <TableCell sx={{ color: styles.color }}>
                      <Chip
                        label={record.status?.toUpperCase()}
                        color={record.status === "paid" ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ color: styles.color }}>
                      {dayjs(record.purchaseDate).format("D MMM YYYY")}
                    </TableCell>
                    <TableCell sx={{ color: styles.color }}>
                      <Stack spacing={1}>
                        {record.product.map((product, idx) => (
                          <Typography key={idx} variant="body2">
                            {product.description}
                          </Typography>
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box
      display={"flex"}
      width="100%"
      flexDirection="column"
      color={styles.color}
      backgroundColor={styles.background}
      overflow="auto"
    >
      {/* Header */}
      <Box display="flex" alignItems="center" gap={3} p="32px 20px 0">
        <IconButton onClick={() => navigate("/profile")}>
          <ChevronLeft sx={{ height: 35, width: 35, fill: styles.color }} />
        </IconButton>
        <Typography variant="h5" fontWeight="500">
          Manage Billing
        </Typography>
      </Box>

      {["Gym", "Affiliate", "Individual"]?.includes(currentUser?.userType) ? (
        <>
          {/* Subscription Info Card */}
          {["active", "canceled", "trialing", "past_due"].includes(
            currentSubscription?.subscriptionStatus
          ) ? (
            <Card
              sx={{
                mx: 3,
                mt: 4,
                background: styles.background,
                color: styles.color
              }}
              elevation={2}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={3}>
                  <CreditCard sx={{ mr: 1, color: "#1976d2" }} />
                  <Typography variant="h6">Current Subscription</Typography>
                </Box>
                <Stack spacing={2}>
                  <Box
                    display="flex"
                    gap={{ xs: 0.5, sm: 10 }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <Typography variant="subtitle1" color={styles.color}>
                      Product
                    </Typography>
                    <Typography variant="body1" fontWeight="500">
                      {
                        currentSubscription?.currentSubscription?.[0]
                          ?.productName
                      }
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    gap={{ xs: 0.5, sm: 10 }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <Typography variant="subtitle1" color={styles.color}>
                      Price
                    </Typography>
                    <Typography variant="body1" fontWeight="500">
                      {currentCurrency?.symbol}
                      {+currentSubscription?.currentSubscription?.[0]?.amount /
                        100}{" "}
                      /{" "}
                      {currentSubscription?.currentSubscription?.[0]?.interval}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    gap={{ xs: 0.5, sm: 10 }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <Typography variant="subtitle1" color={styles.color}>
                      Billing Cycle Start
                    </Typography>
                    <Typography variant="body1">
                      {currentSubscription?.billingCycleStartDate}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    gap={{ xs: 0.5, sm: 10 }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <Typography variant="subtitle1" color={styles.color}>
                      Next Billing Date
                    </Typography>
                    <Typography variant="body1">
                      {currentSubscription?.nextBillingDate}
                    </Typography>
                  </Box>
                  {/* Upgrade Button - Shows when eligible */}
                  {isUpgradeEligible && (
                    <Box 
                      sx={{ 
                        mt: 2, 
                        p: 2, 
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${styles.primaryColor}10 0%, ${styles.primaryColor}10 100%)`,
                        border: "1px solid rgba(151, 154, 168, 0.3)"
                      }}
                    >
                      <Box display="flex" alignItems={{ xs: "flex-start", sm: "center" }} flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" flexWrap="wrap" gap={2}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: styles.color }}>
                            🚀 Upgrade Available!
                          </Typography>
                          <Typography variant="body2" sx={{ color: styles.color, opacity: 0.7 }}>
                            Get our new yearly plan for just $397/year
                          </Typography>
                        </Box>
                        <Button
                          onClick={() => setShowUpgradeModal(true)}
                          sx={{
                            px: 3,
                            py: 1.5,
                            width: { xs: "100%", sm: "auto" },
                            background: `linear-gradient(135deg, ${styles.primaryColor} 0%, ${styles.primaryColor}DD 100%)`,
                            color: "#fff",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                            boxShadow: "0 4px 16px -2px rgba(102, 126, 234, 0.4)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              background: `linear-gradient(135deg, ${styles.primaryColor}DD 0%, ${styles.primaryColor} 100%)`,
                              transform: "translateY(-2px)",
                              boxShadow: "0 6px 20px -2px rgba(102, 126, 234, 0.5)"
                            }
                          }}
                        >
                          View Upgrade
                        </Button>
                      </Box>
                    </Box>
                  )}
                  {currentUser?.isAdmin ||
                  currentSubscription?.subscriptionStatus === "past_due" ? (
                    <></>
                  ) : (
                    <Box display="flex" justifyContent="center">
                      <Button
                        variant="outlined"
                        onClick={() => {
                          if (
                            currentSubscription?.subscriptionStatus ===
                            "canceled"
                          ) {
                            return;
                          } else if (currentSubscription?.canReactivate) {
                            setOpenConfirmModal(true);
                          } else {
                            setOpenModal(true);
                          }
                        }}
                        disabled={
                          loading ||
                          currentSubscription?.subscriptionStatus === "canceled"
                        }
                        startIcon={
                          loading ? <CircularProgress size={20} /> : null
                        }
                        sx={{
                          color:
                            currentSubscription?.canReactivate &&
                            currentSubscription?.subscriptionStatus !== "canceled"
                              ? "#4caf50"
                              : "#FF4000",
                          borderColor:
                            currentSubscription?.canReactivate &&
                            currentSubscription?.subscriptionStatus !== "canceled"
                              ? "#4caf50"
                              : "#FF4000",
                          mt: 2,
                          width: { xs: "100%", sm: "35%" },
                          minWidth: { xs: "100%", sm: "250px" },
                          py: 1.5,
                          textTransform: "none",
                          "&:disabled": {
                            backgroundColor: "#A7C8E9",
                            color: "#64AEDD",
                            pointerEvents: "none"
                          }
                        }}
                      >
                        {currentSubscription?.subscriptionStatus === "canceled"
                          ? "Canceled"
                          : currentSubscription?.canReactivate
                          ? "Reactivate (Scheduled to Cancel)"
                          : "Cancel Subscription"}
                      </Button>
                    </Box>
                  )}
                  {currentSubscription?.subscriptionStatus === "canceled" && (
                    <div className="reactivate-options">
                      <Typography variant="h6">
                        Reactivate Subscription
                      </Typography>
                      <Box display={"flex"} gap={2} mt={2}>
                        <Button
                          sx={{ textTransform: "none", background: "#358ad0" }}
                          variant="contained"
                          onClick={() => setOpenReactivateModal("monthly")}
                          className="btn btn-primary me-2"
                        >
                          Reactivate Monthly
                        </Button>
                        <Button
                          sx={{ textTransform: "none", background: "#358ad0" }}
                          variant="contained"
                          onClick={() => setOpenReactivateModal("yearly")}
                          className="btn btn-success"
                        >
                          Reactivate Yearly
                        </Button>
                      </Box>
                    </div>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Alert severity="info" sx={{ mx: 4.5, mt: 4 }}>
              No active subscription found.
            </Alert>
          )}

          <CardLists setOpenSubscriptionModal={setOpenSubscriptionModal} />
        </>
      ) : (
        <></>
      )}
      {renderBillingHistoryTable()}

      {/* Cancel Subscription Dialog */}
      <Dialog
        TransitionComponent={Transition}
        TransitionProps={{
          timeout: {
            enter: 500,
            exit: 300
          }
        }}
        open={openModal}
        onClose={() => setOpenModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: "400px",
            background: "#1c1c1c"
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
          <Warning sx={{ fontSize: 48, color: "#FF4000", mb: 2 }} />
          <Typography variant="h6" fontWeight="600" color="#FCFCFC">
            Cancel Subscription?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText textAlign="center" color="#FCFCFC">
            This action is permanent and cannot be undone. You will lose access
            to premium features at the end of your billing period.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            sx={{
              background: "#FF4000"
            }}
            fullWidth
            onClick={handleCancelSubscription}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              "Yes, Cancel Subscription"
            )}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setOpenModal(false)}
          >
            No, Keep My Subscription
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        TransitionComponent={Transition}
        TransitionProps={{
          timeout: {
            enter: 500,
            exit: 300
          }
        }}
        open={!!openReactivateModal}
        onClose={() => setOpenReactivateModal(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: "400px",
            background: "#1c1c1c"
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
          <CheckCircle sx={{ fontSize: 48, color: "#358ad0", mb: 2 }} />
          <Typography variant="h6" fontWeight="600" color="#FCFCFC">
            Reactivate Subscription?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText textAlign="center" fontSize={14} color="#FCFCFC">
            This action is permanent and cannot be undone.
            <br /> You will be charged immediately. <br />
            Your subscription cycle will be updated according to your selected
            plan moving forward.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            sx={{
              background: "#358ad0"
            }}
            fullWidth
            onClick={handleRenewSubscription}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              "Yes, Reactivate Subscription"
            )}
          </Button>
          <Button
            variant="outlined"
            sx={{
              margin: "0 !important"
            }}
            fullWidth
            onClick={() => setOpenReactivateModal(null)}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarMessage.includes("success") ? "success" : "error"}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <ConfirmModal
        open={openConfirmModal}
        handleClose={() => setOpenConfirmModal(false)}
        handleConfirm={handleReactivateSubscription}
        styles={styles}
      />
      <UpgradePlanModal
        open={showUpgradeModal}
        handleClose={() => setShowUpgradeModal(false)}
        handleUpgrade={handleUpgradePurchase}
        styles={styles}
        loading={upgradeLoading}
      />
    </Box>
  );
};

export default ManageBilling;
